/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Dialog, IconButton, Button, CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { JobContext } from 'context/jobContextContainer';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { NotificationContext } from 'components/NotificationOverlay';
import { jobsService } from 'services/jobsService';
import { smartMatchService } from 'services/smartMatchService';

const VEHICLE_TYPES = [
  'Town Car', 'Van/Minivan', 'Sedan', 'Stretch', 'Van-Standard', 'Van-Luxury', 'Hybrid',
  'SUV', 'Handicap', 'Compact', 'Taxi', 'Wagon', 'Ambulance', 'Bus', 'Other',
];
const vtLabel = t => (t >= 0 && VEHICLE_TYPES[t] ? VEHICLE_TYPES[t] : 'Any');

const SmartMatchModal = ({ refreshTables }) => {
  const { smartMatchModalInfo, setSmartMatchModalInfo, setSmartMatchSuggestions, smartMatchSuggestions } = useContext(
    JobContext
  );
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { showNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [assigned, setAssigned] = useState(null);
  const [scan, setScan] = useState(null); // 'no_token' | 'service_stopped' | null
  const [expandedId, setExpandedId] = useState(null); // which candidate's factor breakdown is open

  const jobId = smartMatchModalInfo ? smartMatchModalInfo.job_id : null;

  // Compute candidates live when the modal opens / switches job (fresh fleet at decision time).
  useEffect(() => {
    if (!smartMatchModalInfo) return undefined;
    let cancelled = false;
    setLoading(true);
    setCandidates([]);
    setAssigned(null);
    setScan(null);
    setExpandedId(null);
    smartMatchService
      .get_smart_match_candidates(smartMatchModalInfo.job_id)
      .then(({ data }) => {
        if (cancelled) return;
        if (data.status === 'SUCCESS' && data.data) {
          setCandidates(Array.isArray(data.data.candidates) ? data.data.candidates : []);
          setAssigned(data.data.assigned || null);
          setScan(data.data.no_token ? 'no_token' : data.data.service_stopped ? 'service_stopped' : null);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const close = () => setSmartMatchModalInfo(null);

  if (!smartMatchModalInfo) {
    return null;
  }

  const suggestion = smartMatchModalInfo;
  const isLate = suggestion.trigger_type === 'late';
  const passenger = [suggestion.passenger_fname, suggestion.passenger_lname].filter(Boolean).join(' ');
  const currentDriver = [suggestion.current_driver_no, suggestion.current_driver_fname, suggestion.current_driver_lname]
    .filter(Boolean)
    .join(' ');
  const needsWav = parseInt(suggestion.is_handicapped, 10) === 1;

  // Page through all currently-flagged jobs without closing the modal.
  const flags = Array.isArray(smartMatchSuggestions) ? smartMatchSuggestions : [];
  const currentIndex = Math.max(0, flags.findIndex(f => String(f.job_id) === String(suggestion.job_id)));
  const total = flags.length;
  const goJob = delta => {
    if (total <= 1) return;
    setSmartMatchModalInfo(flags[(((currentIndex + delta) % total) + total) % total]);
  };

  // The per-driver factor breakdown shown in a candidate's accordion.
  const factorRows = c => [
    {
      label: 'Proximity to pickup',
      ok: c.factors.proximity >= 0.5,
      detail: `${c.dist_mi} mi from pickup · ~${Math.round(c.eta_min)} min`,
    },
    {
      label: 'Vehicle type & capacity',
      ok: c.factors.vehicle_type >= 0.9,
      detail: `${vtLabel(c.vehicle_type)}${c.seats ? ` · ${c.seats} seats` : ''}`,
    },
    {
      label: 'Driver availability',
      ok: c.factors.availability >= 0.8,
      detail:
        c.source_type === 'soon_free'
          ? `Finishing a trip · free in ~${Math.round(c.available_in_min || 0)} min`
          : c.fresh
          ? 'Idle now · live GPS'
          : 'Idle now · last GPS over 2 min ago',
    },
    {
      label: 'Schedule fit',
      ok: c.factors.schedule >= 0.9,
      detail: c.factors.schedule >= 0.9 ? 'No conflicting upcoming jobs' : 'May conflict with an upcoming reservation',
    },
    {
      label: 'Passenger transport needs',
      ok: c.factors.mobility >= 0.9,
      detail: needsWav ? 'Wheelchair / ambulette capable' : 'Ambulatory · no special assist required',
    },
  ];

  const onDispatch = async cand => {
    showCircularProgress('Dispatching');
    try {
      const res = await jobsService.dispatch_driver_to_job(jobId, cand.driver_id, cand.vehicle_id);
      hideCircularProgress();
      if (res && res.data && res.data.status === 'SUCCESS') {
        showNotification(`Dispatched ${cand.name || cand.driver_no} to Job #${jobId}`, 'success');
        if (refreshTables) refreshTables();
        // Drop the dispatched job; advance to the next flagged one if any, else close.
        const remaining = (smartMatchSuggestions || []).filter(s => String(s.job_id) !== String(jobId));
        setSmartMatchSuggestions(remaining);
        if (remaining.length > 0) {
          setSmartMatchModalInfo(remaining[Math.min(currentIndex, remaining.length - 1)]);
        } else {
          close();
        }
      } else {
        const msg = res && res.data && res.data.data ? res.data.data.msg : 'Unknown error';
        showNotification(`Sorry, failed to dispatch: ${msg}`, 'error');
      }
    } catch (e) {
      hideCircularProgress();
      showNotification('Sorry, failed to dispatch driver.', 'error');
    }
  };

  const onDismiss = () => {
    // Client-side snooze: hide until the next detection poll (it reappears while still due/late).
    setSmartMatchSuggestions((smartMatchSuggestions || []).filter(s => s.job_id !== jobId));
    close();
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="sm-loading">
          <CircularProgress size={34} style={{ color: '#5f9bff' }} />
          <div className="sm-loading-text">Scoring every driver and vehicle in the fleet…</div>
        </div>
      );
    }
    if (scan === 'no_token') {
      return <div className="sm-empty">Smart Match isn&apos;t configured (missing Matrix key). Contact PerGo support.</div>;
    }
    if (scan === 'service_stopped') {
      return <div className="sm-empty">Usage balance depleted — reload in Billing History to use Smart Match.</div>;
    }
    if (candidates.length === 0) {
      return <div className="sm-empty">No available match right now — assign manually.</div>;
    }
    return (
      <div className="sm-matches">
        {candidates.map((c, i) => {
          const open = expandedId === c.driver_id;
          return (
            <div className={`sm-match ${i === 0 ? 'top' : ''}`} key={`${c.driver_id}_${i}`}>
              <div
                className="sm-match-main"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setExpandedId(open ? null : c.driver_id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(open ? null : c.driver_id);
                  }
                }}
              >
                <div className="sm-rank">{i + 1}</div>
                <div className="sm-match-body">
                  <div className="sm-match-head">
                    <span className="sm-match-name">
                      {c.driver_no} — {(c.name || '').toUpperCase()} · {c.vehicle_no} · {vtLabel(c.vehicle_type)}
                    </span>
                    <span className="sm-match-score">{Math.round(c.score)}</span>
                  </div>
                  <div className="sm-match-sub">
                    {c.dist_mi} mi · ~{Math.round(c.eta_min)} min
                    {c.source_type === 'soon_free'
                      ? ` · finishing a trip (free in ~${Math.round(c.available_in_min || 0)} min)`
                      : ''}
                    {typeof c.improves_min !== 'undefined' ? ` · ~${c.improves_min} min sooner` : ''}
                    {c.source_type === 'idle' && !c.fresh ? ' · GPS stale' : ''}
                  </div>
                  <div className="sm-bar">
                    <div className="sm-bar-fill" style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }} />
                  </div>
                </div>
                <div className="sm-match-actions">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onDispatch(c);
                    }}
                    style={{ background: i === 0 ? '#2f6df6' : '#33465c', color: '#fff', minWidth: 88 }}
                  >
                    Dispatch
                  </Button>
                  {open ? <ExpandLessIcon className="sm-expand-ic" /> : <ExpandMoreIcon className="sm-expand-ic" />}
                </div>
              </div>
              {open && (
                <div className="sm-accordion">
                  {factorRows(c).map(r => (
                    <div className="sm-score-row" key={r.label}>
                      {r.ok ? <CheckCircleIcon className="sm-ic ok" /> : <ErrorOutlineIcon className="sm-ic warn" />}
                      <div className="sm-score-text">
                        <div className="sm-score-label">{r.label}</div>
                        <div className="sm-score-detail">{r.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={!!smartMatchModalInfo}
      onClose={close}
      PaperProps={{ style: { background: '#0d1b2a', color: '#e8eef5', borderRadius: 12 } }}
    >
      <Wrapper>
        <div className="sm-header">
          <div>
            <div className="sm-title">Smart Match — Job #{jobId}</div>
            <div className="sm-subtitle">
              {loading
                ? 'Scoring every driver and vehicle in the fleet'
                : candidates.length
                ? 'Best matches — expand any driver to see why'
                : 'Smart Match'}
            </div>
          </div>
          <div className="sm-header-actions">
            {total > 1 && (
              <span className="sm-nav">
                <IconButton size="small" aria-label="previous job" onClick={() => goJob(-1)} style={{ color: '#8aa0b6' }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <span className="sm-nav-count">
                  {currentIndex + 1} of {total}
                </span>
                <IconButton size="small" aria-label="next job" onClick={() => goJob(1)} style={{ color: '#8aa0b6' }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            )}
            <IconButton size="small" onClick={close} style={{ color: '#8aa0b6' }}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {(passenger || isLate) && (
          <div className="sm-meta">
            {passenger ? <span>Passenger: {passenger}</span> : null}
            {isLate && currentDriver ? (
              <span className="sm-warn">
                <ErrorOutlineIcon style={{ fontSize: 16, marginRight: 4 }} />
                Currently {currentDriver}
                {assigned && typeof assigned.delay_min !== 'undefined' ? ` — arriving ~${assigned.delay_min} min late` : ''}
                ; dispatching removes and notifies them.
              </span>
            ) : null}
          </div>
        )}

        {renderBody()}

        {!loading && candidates.length > 0 && (
          <div className="sm-actions">
            <Button onClick={onDismiss} style={{ color: '#8aa0b6' }}>
              Dismiss
            </Button>
          </div>
        )}
      </Wrapper>
    </Dialog>
  );
};

export default SmartMatchModal;

const Wrapper = styled.div`
  padding: 18px 20px 14px;

  .sm-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .sm-header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .sm-nav {
    display: inline-flex;
    align-items: center;
  }
  .sm-nav-count {
    font-size: 12px;
    color: #8aa0b6;
    white-space: nowrap;
    min-width: 46px;
    text-align: center;
  }
  .sm-title {
    font-size: 20px;
    font-weight: 700;
  }
  .sm-subtitle {
    color: #5f9bff;
    font-size: 13px;
    margin-top: 2px;
  }
  .sm-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 10px 0 4px;
    font-size: 13px;
    color: #b7c6d6;
  }
  .sm-warn {
    display: inline-flex;
    align-items: center;
    color: #ffb84d;
  }
  .sm-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 0;
  }
  .sm-loading-text {
    color: #8aa0b6;
    font-size: 14px;
  }
  .sm-empty {
    padding: 30px 0;
    text-align: center;
    color: #8aa0b6;
  }
  .sm-matches {
    margin: 12px 0 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sm-match {
    display: flex;
    flex-direction: column;
    background: #12263a;
    border: 1px solid transparent;
    border-radius: 10px;
    padding: 10px 12px;
  }
  .sm-match.top {
    border-color: #2f6df6;
    background: #14304d;
  }
  .sm-match-main {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
  }
  .sm-match-main:focus {
    outline: none;
  }
  .sm-match-main:focus-visible {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
    border-radius: 6px;
  }
  .sm-expand-ic {
    color: #8aa0b6;
    flex-shrink: 0;
  }
  .sm-rank {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #33465c;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .sm-match.top .sm-rank {
    background: #2f6df6;
  }
  .sm-match-body {
    flex: 1;
    min-width: 0;
  }
  .sm-match-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
  }
  .sm-match-name {
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sm-match-score {
    font-weight: 700;
    font-size: 18px;
    flex-shrink: 0;
  }
  .sm-match-sub {
    color: #8aa0b6;
    font-size: 12px;
    margin: 2px 0 6px;
  }
  .sm-bar {
    height: 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }
  .sm-bar-fill {
    height: 100%;
    background: #5f9bff;
  }
  .sm-match-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
  .sm-accordion {
    margin-top: 10px;
    padding-top: 2px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
  .sm-score-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
  .sm-score-row:first-child {
    border-top: none;
  }
  .sm-ic {
    margin-right: 12px;
  }
  .sm-ic.ok {
    color: #34c759;
  }
  .sm-ic.warn {
    color: #ffb84d;
  }
  .sm-score-label {
    font-weight: 600;
    font-size: 14px;
  }
  .sm-score-detail {
    color: #8aa0b6;
    font-size: 12px;
  }
  .sm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }
`;
